import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../App';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import TwoFactorSetup from '../../components/TwoFactorSetup';
import {
  Home, Heart, Calendar, Gavel, History, Bell, User, LogOut, Menu, X,
  Building, MapPin, Clock, ChevronRight, Phone, CreditCard, CheckCircle, AlertCircle, Loader2, Shield,
  FileText, Settings, Download, Trash2, ArrowLeft
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

// Sidebar Component
const Sidebar = ({ isOpen, setIsOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const menuItems = [
    { path: '/buyer', icon: Home, label: 'Dashboard', exact: true },
    { path: '/buyer/profilo', icon: User, label: 'Profilo' },
    { path: '/buyer/sicurezza', icon: Shield, label: 'Sicurezza' },
    { path: '/buyer/preferiti', icon: Heart, label: 'Preferiti' },
    { path: '/buyer/visite', icon: Calendar, label: 'Visite' },
    { path: '/buyer/gare', icon: Building, label: 'Le Mie Gare' },
    { path: '/buyer/offerte', icon: History, label: 'Storico Offerte' },
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden" 
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-72 bg-[#F5F5F7] z-50 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6">
            <Link to="/" className="flex items-center">
              <img 
                src="/weqasa-logo.png" 
                alt="WEQASA" 
                className="h-10 w-auto object-contain"
              />
            </Link>
          </div>

          {/* User Info */}
          <div className="px-6 pb-6">
            <div className="flex items-center space-x-3 p-4 bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)]">
              <div className="w-10 h-10 bg-[#0A2540]/10 rounded-xl flex items-center justify-center">
                <User className="h-5 w-5 text-[#0A2540]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-semibold text-[#1D1D1F] truncate">
                  {user?.first_name} {user?.last_name}
                </p>
                <p className="text-[12px] text-[#86868B] truncate">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                  isActive(item.path, item.exact)
                    ? 'bg-white text-[#1D1D1F] shadow-[0_2px_8px_rgb(0,0,0,0.04)]'
                    : 'text-[#86868B] hover:bg-white/50 hover:text-[#1D1D1F]'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="h-5 w-5" strokeWidth={1.5} />
                <span className="text-[14px] font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-4">
            <button
              onClick={handleLogout}
              className="flex items-center space-x-3 px-4 py-3 w-full text-[#86868B] hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200"
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5" strokeWidth={1.5} />
              <span className="text-[14px] font-medium">Esci</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Overview
const DashboardOverview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    favorites: 0,
    visits: 0,
    participations: 0,
    bids: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [favRes, visitsRes, partRes, bidsRes] = await Promise.all([
        axios.get('/favorites'),
        axios.get('/visits/buyer'),
        axios.get('/participation/buyer'),
        axios.get('/bids/buyer/history')
      ]);

      setStats({
        favorites: favRes.data.length,
        visits: visitsRes.data.length,
        participations: partRes.data.length,
        bids: bidsRes.data.length
      });

      // Combine and sort recent activity
      const activities = [
        ...visitsRes.data.slice(0, 3).map(v => ({ ...v, type: 'visit' })),
        ...partRes.data.slice(0, 3).map(p => ({ ...p, type: 'participation' })),
        ...bidsRes.data.slice(0, 3).map(b => ({ ...b, type: 'bid' }))
      ].sort((a, b) => new Date(b.created_at || b.timestamp) - new Date(a.created_at || a.timestamp)).slice(0, 5);

      setRecentActivity(activities);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="space-y-8" data-testid="buyer-dashboard">
      {/* Welcome Card - Apple Style */}
      <div className="relative overflow-hidden bg-gradient-to-br from-[#1D1D1F] to-[#2D2D2F] rounded-3xl p-8">
        <div className="relative z-10">
          <p className="text-[#86868B] text-sm font-medium mb-2">Bentornato</p>
          <h1 className="text-3xl font-semibold text-white tracking-tight mb-3">
            Ciao, {user?.first_name}
          </h1>
          <p className="text-[#A1A1A6] text-lg max-w-md">
            Gestisci le tue attività e partecipa alle Gare di Vendita immobiliari.
          </p>
          {!user?.phone_verified && (
            <div className="mt-6 inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-2xl px-5 py-3">
              <Phone className="h-5 w-5 text-[#0071e3]" />
              <span className="text-white text-sm">Verifica il telefono per partecipare alle gare</span>
            </div>
          )}
        </div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#0071e3]/10 rounded-full blur-3xl" />
      </div>

      {/* Stats Grid - Apple Style */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link 
          to="/buyer/preferiti" 
          className="group bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300"
        >
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
            <Heart className="h-6 w-6 text-red-500" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">{stats.favorites}</div>
          <div className="text-sm text-[#86868B] mt-1">Preferiti</div>
        </Link>
        <Link 
          to="/buyer/visite" 
          className="group bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300"
        >
          <div className="w-12 h-12 rounded-2xl bg-blue-50 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
            <Calendar className="h-6 w-6 text-[#0071e3]" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">{stats.visits}</div>
          <div className="text-sm text-[#86868B] mt-1">Visite</div>
        </Link>
        <Link 
          to="/buyer/gare" 
          className="group bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300"
        >
          <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
            <Gavel className="h-6 w-6 text-[#0071e3]" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">{stats.participations}</div>
          <div className="text-sm text-[#86868B] mt-1">Partecipazioni</div>
        </Link>
        <Link 
          to="/buyer/offerte" 
          className="group bg-white rounded-2xl p-6 shadow-[0_2px_8px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300"
        >
          <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300">
            <History className="h-6 w-6 text-green-600" strokeWidth={1.5} />
          </div>
          <div className="text-3xl font-semibold text-[#1D1D1F] tracking-tight">{stats.bids}</div>
          <div className="text-sm text-[#86868B] mt-1">Offerte</div>
        </Link>
      </div>

      {/* Recent Activity - Apple Style */}
      <div className="bg-white rounded-3xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="px-6 py-5 border-b border-[#E5E5EA]">
          <h2 className="text-lg font-semibold text-[#1D1D1F] tracking-tight">Attività Recente</h2>
        </div>
        {recentActivity.length > 0 ? (
          <div className="divide-y divide-[#E5E5EA]">
            {recentActivity.map((activity, index) => (
              <div key={index} className="px-6 py-4 flex items-center space-x-4 hover:bg-[#F5F5F7] transition-colors duration-200">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                  activity.type === 'visit' ? 'bg-blue-50' :
                  activity.type === 'participation' ? 'bg-amber-50' : 'bg-green-50'
                }`}>
                  {activity.type === 'visit' && <Calendar className="h-5 w-5 text-[#0071e3]" strokeWidth={1.5} />}
                  {activity.type === 'participation' && <Gavel className="h-5 w-5 text-[#0071e3]" strokeWidth={1.5} />}
                  {activity.type === 'bid' && <History className="h-5 w-5 text-green-600" strokeWidth={1.5} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#1D1D1F] truncate">
                    {activity.property?.title || 'Immobile'}
                  </p>
                  <p className="text-sm text-[#86868B]">
                    {activity.type === 'visit' && `Visita - ${activity.status}`}
                    {activity.type === 'participation' && `Partecipazione - ${activity.participation_status}`}
                    {activity.type === 'bid' && `Offerta - ${formatPrice(activity.bid_amount)}`}
                  </p>
                </div>
                <ChevronRight className="h-5 w-5 text-[#C7C7CC]" />
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-[#F5F5F7] flex items-center justify-center mx-auto mb-4">
              <Building className="h-8 w-8 text-[#86868B]" strokeWidth={1.5} />
            </div>
            <p className="text-[#86868B] mb-4">Nessuna attività recente</p>
            <Link 
              to="/immobili" 
              className="inline-flex items-center text-[#0071e3] font-medium hover:underline"
            >
              Esplora gli immobili
              <ChevronRight className="h-4 w-4 ml-1" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

// Profile Page
const ProfilePage = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#1D1D1F] tracking-tight">Il Mio Profilo</h1>
      </div>
      
      <div className="bg-white rounded-3xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="p-8">
          <div className="flex items-center space-x-6 mb-8">
            <div className="w-20 h-20 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center">
              <User className="h-10 w-10 text-[#0071e3]" strokeWidth={1.5} />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#1D1D1F]">{user?.first_name} {user?.last_name}</h2>
              <p className="text-[#86868B]">{user?.email}</p>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#86868B]">Nome</label>
              <p className="text-[#1D1D1F] font-medium">{user?.first_name}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#86868B]">Cognome</label>
              <p className="text-[#1D1D1F] font-medium">{user?.last_name}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#86868B]">Email</label>
              <div className="flex items-center space-x-2">
                <p className="text-[#1D1D1F] font-medium">{user?.email}</p>
                {user?.email_verified ? (
                  <Badge className="bg-green-50 text-green-700 border-0">Verificata</Badge>
                ) : (
                  <Badge className="bg-amber-50 text-amber-700 border-0">Da verificare</Badge>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#86868B]">Telefono</label>
              <div className="flex items-center space-x-2">
                <p className="text-[#1D1D1F] font-medium">{user?.phone || 'Non inserito'}</p>
                {user?.phone_verified ? (
                  <Badge className="bg-green-50 text-green-700 border-0">Verificato</Badge>
                ) : (
                  <Badge className="bg-amber-50 text-amber-700 border-0">Da verificare</Badge>
                )}
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#86868B]">Codice Fiscale</label>
              <p className="text-[#1D1D1F] font-medium">{user?.tax_code || 'Non inserito'}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-[#86868B]">Stato Account</label>
              <Badge className={user?.profile_status === 'active' ? 'bg-green-50 text-green-700 border-0' : 'bg-amber-50 text-amber-700 border-0'}>
                {user?.profile_status || 'Registrato'}
              </Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Favorites Page
const FavoritesPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      const response = await axios.get('/favorites');
      setFavorites(response.data);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (propertyId) => {
    try {
      await axios.delete(`/favorites/${propertyId}`);
      setFavorites(favorites.filter(f => f.property_id !== propertyId));
      toast.success('Rimosso dai preferiti');
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-[#86868B]" />
    </div>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] tracking-tight">I Miei Preferiti</h1>
      
      {favorites.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-6">
          {favorites.map((property) => (
            <div key={property.property_id} className="bg-white rounded-3xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-shadow duration-300">
              <div className="aspect-video bg-[#F5F5F7]">
                <img 
                  src={property.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400'} 
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-[#1D1D1F] text-lg">{property.title}</h3>
                <p className="text-sm text-[#86868B] flex items-center mt-1">
                  <MapPin className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  {property.city}
                </p>
                <div className="flex justify-between items-center mt-4 pt-4 border-t border-[#E5E5EA]">
                  <Link 
                    to={`/immobili/${property.property_id}`}
                    className="text-[#0071e3] hover:underline text-sm font-medium"
                  >
                    Vedi dettagli
                  </Link>
                  <button
                    onClick={() => removeFavorite(property.property_id)}
                    className="text-red-500 hover:text-red-700 text-sm font-medium"
                  >
                    Rimuovi
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
            <Heart className="h-8 w-8 text-red-300" strokeWidth={1.5} />
          </div>
          <p className="text-[#86868B] mb-4">Non hai ancora aggiunto preferiti</p>
          <Link 
            to="/immobili" 
            className="inline-flex items-center text-[#0071e3] font-medium hover:underline"
          >
            Esplora gli immobili
            <ChevronRight className="h-4 w-4 ml-1" />
          </Link>
        </div>
      )}
    </div>
  );
};

// Visits Page
const VisitsPage = () => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchVisits();
  }, []);

  const fetchVisits = async () => {
    try {
      // Use enhanced endpoint with gara info
      const response = await axios.get('/buyer/visits/with-gare');
      setVisits(response.data);
    } catch (error) {
      // Fallback to basic endpoint
      try {
        const fallback = await axios.get('/visits/buyer');
        setVisits(fallback.data);
      } catch (e) {
        console.error('Error fetching visits:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="badge-active">Confermata</Badge>;
      case 'pending': return <Badge className="badge-pending">In Attesa</Badge>;
      case 'cancelled': return <Badge className="badge-closed">Annullata</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  const formatGaraTime = (isoTime) => {
    if (!isoTime) return null;
    try {
      return format(new Date(isoTime), "dd/MM 'alle' HH:mm", { locale: it });
    } catch {
      return null;
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6" data-testid="buyer-visits-page">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Le Mie Visite</h1>
      
      {visits.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
          <div className="divide-y divide-gray-100">
            {visits.map((visit) => (
              <div key={visit.visit_id} className="p-4" data-testid={`visit-${visit.visit_id}`}>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                      src={visit.property?.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=100'} 
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-[#1D1D1F]">{visit.property?.title}</h3>
                      <div className="flex items-center space-x-2">
                        {visit.gara && (
                          <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                            <Gavel className="h-3 w-3 mr-1" />
                            Gara
                          </Badge>
                        )}
                        {getStatusBadge(visit.status)}
                      </div>
                    </div>
                    <p className="text-sm text-[#86868B] flex items-center mt-1">
                      <Calendar className="h-4 w-4 mr-1" />
                      {format(new Date(visit.visit_date), 'dd/MM/yyyy', { locale: it })} - {visit.visit_time}
                    </p>
                    
                    {/* Gara info section */}
                    {visit.gara && (
                      <div className="mt-3 p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-100">
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            <span className="text-purple-700 font-medium">Gara di Vendita Attiva</span>
                            {visit.gara.end_time && (
                              <span className="text-gray-600 ml-2">
                                • Termina il {formatGaraTime(visit.gara.end_time)}
                              </span>
                            )}
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-purple-600 border-purple-200 hover:bg-purple-50"
                            onClick={() => navigate(`/gara/${visit.gara.competition_id}`)}
                          >
                            <Gavel className="h-3 w-3 mr-1" />
                            Partecipa
                          </Button>
                        </div>
                        {visit.gara.base_price && (
                          <p className="text-xs text-[#86868B] mt-2">
                            Base di gara: €{visit.gara.base_price.toLocaleString('it-IT')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-8 text-center">
          <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-[#86868B]">Nessuna visita prenotata</p>
          <Link to="/immobili" className="text-[#0071e3] hover:underline mt-2 inline-block">
            Prenota una visita
          </Link>
        </div>
      )}
    </div>
  );
};

// Participations Page
const ParticipationsPage = () => {
  const [participations, setParticipations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingDeposit, setPayingDeposit] = useState(null);
  const [depositDialogOpen, setDepositDialogOpen] = useState(false);
  const [selectedParticipation, setSelectedParticipation] = useState(null);
  const location = useLocation();

  useEffect(() => {
    fetchParticipations();
    // Check for payment status in URL
    const params = new URLSearchParams(location.search);
    if (params.get('deposit') === 'success') {
      toast.success('Deposito pagato con successo! La tua partecipazione è ora attiva.');
      window.history.replaceState({}, '', '/buyer/partecipazioni');
      fetchParticipations();
    } else if (params.get('deposit') === 'cancelled') {
      toast.error('Pagamento annullato');
      window.history.replaceState({}, '', '/buyer/partecipazioni');
    }
  }, [location]);

  const fetchParticipations = async () => {
    try {
      const response = await axios.get('/participation/buyer');
      setParticipations(response.data);
    } catch (error) {
      console.error('Error fetching participations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePayDeposit = async (participation) => {
    setPayingDeposit(participation.participation_id);
    try {
      const response = await axios.post('/payments/deposit/checkout', {
        property_id: participation.property_id,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Errore nella creazione del pagamento');
      }
    } catch (error) {
      console.error('Deposit payment error:', error);
      toast.error(error.response?.data?.detail || 'Errore durante il pagamento del deposito');
    } finally {
      setPayingDeposit(null);
    }
  };

  const openDepositDialog = (participation) => {
    setSelectedParticipation(participation);
    setDepositDialogOpen(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <Badge className="badge-active">Approvata</Badge>;
      case 'pending': return <Badge className="badge-pending">In Attesa</Badge>;
      case 'rejected': return <Badge className="badge-closed">Rifiutata</Badge>;
      default: return <Badge>{status}</Badge>;
    }
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Le Mie Partecipazioni</h1>
        <p className="text-[#86868B] mt-1">Gestisci le tue partecipazioni alle gare di vendita</p>
      </div>
      
      {participations.length > 0 ? (
        <div className="space-y-4">
          {participations.map((part) => (
            <div key={part.participation_id} className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building className="h-8 w-8 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[#1D1D1F]">{part.property?.title || 'Immobile'}</h3>
                    <p className="text-[#86868B] flex items-center mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      {part.property?.address}, {part.property?.city}
                    </p>
                    {part.property?.auction_start_price && (
                      <p className="text-[#0071e3] font-semibold mt-1">
                        Base di gara: {formatPrice(part.property.auction_start_price)}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  {getStatusBadge(part.participation_status)}
                  <span className={`text-sm font-medium ${part.deposit_paid ? 'text-green-600' : 'text-[#0071e3]'}`}>
                    {part.deposit_paid ? (
                      <span className="flex items-center"><CheckCircle className="h-4 w-4 mr-1" /> Deposito versato</span>
                    ) : (
                      <span className="flex items-center"><AlertCircle className="h-4 w-4 mr-1" /> Deposito da versare</span>
                    )}
                  </span>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="text-sm text-[#86868B]">
                  {part.deposit_amount && (
                    <span>Deposito richiesto: <strong className="text-[#1D1D1F]">{formatPrice(part.deposit_amount)}</strong></span>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  {!part.deposit_paid && part.participation_status === 'approved' && (
                    <Button
                      onClick={() => openDepositDialog(part)}
                      className="bg-[#0071e3] hover:bg-[#005BB5]"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Paga Deposito
                    </Button>
                  )}
                  <Link 
                    to={`/immobili/${part.property_id}`}
                    className="text-[#0071e3] hover:underline flex items-center text-sm"
                  >
                    Vai all'immobile <ChevronRight className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Gavel className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna partecipazione</h3>
          <p className="text-[#86868B] mb-4">Non hai ancora richiesto di partecipare a nessuna gara di vendita</p>
          <Link to="/immobili">
            <Button className="bg-[#0071e3] hover:bg-[#0077ED]">
              Esplora gli immobili disponibili
            </Button>
          </Link>
        </div>
      )}

      {/* Deposit Payment Dialog */}
      <Dialog open={depositDialogOpen} onOpenChange={setDepositDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <CreditCard className="h-5 w-5 text-[#0071e3]" />
              <span>Pagamento Deposito Cauzionale</span>
            </DialogTitle>
            <DialogDescription>
              Per partecipare alla gara di vendita è necessario versare un deposito cauzionale.
            </DialogDescription>
          </DialogHeader>
          
          {selectedParticipation && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-[#1D1D1F] mb-2">{selectedParticipation.property?.title || 'Immobile'}</h4>
                <p className="text-sm text-[#86868B] flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {selectedParticipation.property?.city}
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Importo deposito</span>
                  <span className="text-2xl font-bold text-[#0071e3]">
                    {formatPrice(selectedParticipation.deposit_amount || 5000)}
                  </span>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                <p><strong>Nota:</strong> Il deposito verrà rimborsato se non risulterai vincitore della gara di vendita.</p>
              </div>

              <div className="flex space-x-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setDepositDialogOpen(false)}
                >
                  Annulla
                </Button>
                <Button 
                  className="flex-1 bg-[#0071e3] hover:bg-[#005BB5]"
                  onClick={() => {
                    setDepositDialogOpen(false);
                    handlePayDeposit(selectedParticipation);
                  }}
                  disabled={payingDeposit === selectedParticipation?.participation_id}
                >
                  {payingDeposit === selectedParticipation?.participation_id ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  Procedi al Pagamento
                </Button>
              </div>

              <p className="text-xs text-[#86868B] text-center">
                Pagamento sicuro con Stripe
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Buyer Gare Page (Le Mie Gare di Vendita)
const BuyerGarePage = () => {
  const [gare, setGare] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGare();
  }, []);

  const fetchGare = async () => {
    try {
      const response = await axios.get('/buyer/gare');
      setGare(response.data);
    } catch (error) {
      console.error('Error fetching gare:', error);
      toast.error('Errore nel caricamento delle gare');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      scheduled: { class: 'bg-blue-100 text-blue-800', label: 'Programmata' },
      active: { class: 'bg-green-100 text-green-800', label: 'In Corso' },
      closing: { class: 'bg-yellow-100 text-yellow-800', label: 'In Chiusura' },
      suspended: { class: 'bg-red-100 text-red-800', label: 'Sospesa' },
      awarded: { class: 'bg-purple-100 text-purple-800', label: 'Aggiudicata' },
      not_awarded: { class: 'bg-gray-100 text-gray-800', label: 'Non Aggiudicata' },
      under_review: { class: 'bg-orange-100 text-orange-800', label: 'In Revisione' },
      cancelled: { class: 'bg-red-100 text-red-800', label: 'Annullata' }
    };
    const config = statusConfig[status] || { class: 'bg-gray-100', label: status };
    return <Badge className={config.class}>{config.label}</Badge>;
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto text-[#0071e3]" /></div>;

  return (
    <div className="space-y-6" data-testid="buyer-gare-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Le Mie Gare</h1>
          <p className="text-[#86868B] mt-1">Gare di vendita a cui stai partecipando</p>
        </div>
        <Button onClick={() => navigate('/gare')} className="bg-[#0071e3] hover:bg-[#0077ED]">
          <Gavel className="h-4 w-4 mr-2" />
          Esplora Gare
        </Button>
      </div>
      
      {gare.length > 0 ? (
        <div className="space-y-4">
          {gare.map((gara) => (
            <div 
              key={gara.competition_id} 
              className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6 hover:shadow-md transition-shadow"
              data-testid={`buyer-gara-${gara.competition_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                    {gara.property?.photos?.[0] ? (
                      <img src={gara.property.photos[0]} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Building className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-[#1D1D1F]">
                      {gara.property?.address || 'Immobile'}
                    </h3>
                    <p className="text-[#86868B] flex items-center mt-1">
                      <MapPin className="h-4 w-4 mr-1" />
                      {gara.property?.city}
                    </p>
                    <div className="flex items-center gap-4 mt-2 text-sm">
                      <span className="text-[#86868B]">
                        Base: <strong className="text-[#1D1D1F]">{formatPrice(gara.base_di_gara)}</strong>
                      </span>
                      {gara.current_highest_bid && (
                        <span className="text-green-600">
                          Attuale: <strong>{formatPrice(gara.current_highest_bid)}</strong>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(gara.status)}
                  {gara.am_i_winning && (
                    <Badge className="bg-green-500 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Stai Vincendo!
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm text-[#86868B]">
                  {gara.my_highest_bid && (
                    <span>
                      La tua offerta più alta: <strong className="text-[#0071e3]">{formatPrice(gara.my_highest_bid)}</strong>
                    </span>
                  )}
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {gara.total_bids || 0} offerte totali
                  </span>
                </div>
                <Button
                  variant="outline"
                  onClick={() => navigate(`/gara/${gara.competition_id}`)}
                  className="border-[#0071e3] text-[#0071e3]"
                  data-testid={`view-gara-btn-${gara.competition_id}`}
                >
                  {gara.status === 'active' ? 'Vai alla Gara' : 'Vedi Dettagli'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-12 text-center">
          <Gavel className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Nessuna gara attiva</h3>
          <p className="text-[#86868B] mb-6">Non stai partecipando a nessuna gara di vendita.</p>
          <Button onClick={() => navigate('/gare')} className="bg-[#0071e3] hover:bg-[#0077ED]">
            <Gavel className="h-4 w-4 mr-2" />
            Esplora le Gare Disponibili
          </Button>
        </div>
      )}
    </div>
  );
};

// Bids History Page
const BidsHistoryPage = () => {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBids();
  }, []);

  const fetchBids = async () => {
    try {
      const response = await axios.get('/bids/buyer/history');
      setBids(response.data);
    } catch (error) {
      console.error('Error fetching bids:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(price);
  };

  if (loading) return <div className="text-center py-8">Caricamento...</div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-[#1D1D1F] ">Storico Offerte</h1>
      
      {bids.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#86868B] uppercase">Immobile</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#86868B] uppercase">Offerta</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#86868B] uppercase">Data</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[#86868B] uppercase">Stato Gara</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bids.map((bid) => (
                <tr key={bid.bid_id}>
                  <td className="px-6 py-4">
                    <Link to={`/immobili/${bid.property_id}`} className="text-[#0071e3] hover:underline">
                      {bid.property?.title || 'Immobile'}
                    </Link>
                  </td>
                  <td className="px-6 py-4 font-semibold tabular-nums">{formatPrice(bid.bid_amount)}</td>
                  <td className="px-6 py-4 text-[#86868B]">
                    {format(new Date(bid.timestamp), 'dd/MM/yyyy HH:mm', { locale: it })}
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={
                      bid.property?.auction_status === 'active' ? 'badge-active' :
                      bid.property?.auction_status === 'closed' ? 'badge-closed' : 'badge-pending'
                    }>
                      {bid.property?.auction_status || 'N/D'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-8 text-center">
          <History className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-[#86868B]">Nessuna offerta effettuata</p>
        </div>
      )}
    </div>
  );
};

// Security Page with 2FA
const SecurityPage = () => {
  const { token } = useAuth();

  return (
    <div className="space-y-8" data-testid="security-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#1D1D1F] tracking-tight mb-2">Sicurezza</h1>
        <p className="text-[#86868B]">Gestisci le impostazioni di sicurezza del tuo account</p>
      </div>

      {/* 2FA Section */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] overflow-hidden">
        <div className="p-6 border-b border-[#E5E5EA]">
          <h2 className="text-lg font-semibold text-[#1D1D1F] flex items-center space-x-2">
            <Shield className="h-5 w-5 text-[#0071e3]" />
            <span>Autenticazione a due fattori (2FA)</span>
          </h2>
          <p className="text-sm text-[#86868B] mt-1">
            Aggiungi un ulteriore livello di sicurezza al tuo account
          </p>
        </div>
        <div className="p-6">
          <TwoFactorSetup token={token} />
        </div>
      </div>

      {/* Privacy Link */}
      <Link 
        to="/buyer/privacy"
        className="block bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-2xl bg-[#0071e3]/10 flex items-center justify-center">
              <User className="h-6 w-6 text-[#0071e3]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#1D1D1F]">Privacy e dati personali</h3>
              <p className="text-sm text-[#86868B]">Gestisci i tuoi dati e le preferenze GDPR</p>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[#86868B]" />
        </div>
      </Link>
    </div>
  );
};

// Buyer Privacy Dashboard Page
const BuyerPrivacyDashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState({
    marketing_emails: false,
    analytics_tracking: true,
    third_party_sharing: false,
    personalized_content: true
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);

  const handlePreferenceChange = async (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    toast.success('Preferenza aggiornata');
  };

  const handleExportData = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Richiesta di esportazione dati inviata. Riceverai un\'email con il link per scaricare i tuoi dati.');
      setShowExportDialog(false);
    } catch (error) {
      toast.error('Errore nella richiesta di esportazione');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      toast.success('Richiesta di eliminazione account inviata. Sarai contattato per confermare l\'operazione.');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Errore nella richiesta di eliminazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/buyer/sicurezza')} className="p-2">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold text-[#1D1D1F]">Privacy e Dati Personali</h1>
          <p className="text-[#86868B] mt-1">Gestisci i tuoi dati secondo il GDPR (Reg. UE 2016/679)</p>
        </div>
      </div>

      {/* GDPR Rights Info */}
      <div className="bg-[#0071e3]/5 border border-[#0071e3]/20 rounded-2xl p-6">
        <div className="flex items-start gap-4">
          <Shield className="h-8 w-8 text-[#0071e3] flex-shrink-0" />
          <div>
            <h2 className="font-semibold text-[#1D1D1F] mb-2">I tuoi diritti secondo il GDPR</h2>
            <p className="text-sm text-gray-600 leading-relaxed">
              In qualità di cittadino dell'Unione Europea, hai diritto di accedere, rettificare, cancellare 
              e portare i tuoi dati personali. Puoi anche opporti al trattamento e revocare il consenso in qualsiasi momento.
            </p>
          </div>
        </div>
      </div>

      {/* Data Overview */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#0071e3]" />
          Dati in nostro possesso
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati identificativi</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Nome e cognome</li>
              <li>• Indirizzo email: {user?.email}</li>
              <li>• Numero di telefono (se fornito)</li>
              <li>• Codice fiscale (se fornito)</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati di utilizzo</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Log di accesso</li>
              <li>• Immobili visualizzati e preferiti</li>
              <li>• Partecipazioni alle gare</li>
              <li>• Cronologia offerte</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati di ricerca</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Preferenze di ricerca</li>
              <li>• Zone di interesse</li>
              <li>• Budget e criteri</li>
            </ul>
          </div>
          <div className="bg-gray-50 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Dati finanziari</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Metodo di pagamento (parzialmente mascherato)</li>
              <li>• Caparre versate</li>
              <li>• Storico transazioni</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Consent Preferences */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <Settings className="h-5 w-5 text-[#0071e3]" />
          Preferenze di consenso
        </h2>
        <div className="space-y-4">
          {[
            { key: 'marketing_emails', title: 'Email di marketing', description: 'Ricevi aggiornamenti su nuovi immobili e promozioni' },
            { key: 'analytics_tracking', title: 'Analytics e miglioramento servizio', description: 'Ci permette di migliorare l\'esperienza sulla piattaforma' },
            { key: 'third_party_sharing', title: 'Condivisione con terze parti', description: 'Condivisione dati con partner per servizi aggiuntivi' },
            { key: 'personalized_content', title: 'Contenuti personalizzati', description: 'Mostra immobili basati sulle tue preferenze di ricerca' }
          ].map((pref) => (
            <div key={pref.key} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <div className="flex-1">
                <h3 className="font-medium text-[#1D1D1F]">{pref.title}</h3>
                <p className="text-sm text-gray-500">{pref.description}</p>
              </div>
              <button
                onClick={() => handlePreferenceChange(pref.key, !preferences[pref.key])}
                className={`relative w-12 h-6 rounded-full transition-colors ${preferences[pref.key] ? 'bg-[#0071e3]' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${preferences[pref.key] ? 'translate-x-7' : 'translate-x-1'}`} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Data Actions */}
      <div className="bg-white rounded-2xl shadow-[0_2px_8px_rgb(0,0,0,0.04)] p-6">
        <h2 className="font-semibold text-lg text-[#1D1D1F] mb-4 flex items-center gap-2">
          <Download className="h-5 w-5 text-[#0071e3]" />
          Azioni sui tuoi dati
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="border border-gray-200 rounded-xl p-4">
            <h3 className="font-medium text-[#1D1D1F] mb-2">Esporta i tuoi dati</h3>
            <p className="text-sm text-gray-500 mb-4">Scarica una copia di tutti i dati (Art. 20 GDPR)</p>
            <Button variant="outline" onClick={() => setShowExportDialog(true)}>
              <Download className="h-4 w-4 mr-2" />
              Richiedi esportazione
            </Button>
          </div>
          <div className="border border-red-200 rounded-xl p-4 bg-red-50/50">
            <h3 className="font-medium text-red-800 mb-2">Elimina account e dati</h3>
            <p className="text-sm text-red-600 mb-4">Elimina definitivamente account e dati (Art. 17 GDPR)</p>
            <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-100" onClick={() => setShowDeleteDialog(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Richiedi eliminazione
            </Button>
          </div>
        </div>
      </div>

      {/* Legal References */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h2 className="font-semibold text-[#1D1D1F] mb-4">Riferimenti normativi</h2>
        <p className="text-sm text-gray-600">
          Per esercitare i tuoi diritti o per domande sulla privacy: <strong>privacy@weqasa.it</strong>
        </p>
        <p className="text-xs text-gray-500 mt-4">
          Titolare del trattamento: WEQASA S.r.l.
        </p>
      </div>

      {/* Dialogs */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esporta i tuoi dati</DialogTitle>
            <DialogDescription>Riceverai un'email con un link per scaricare i tuoi dati.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportDialog(false)}>Annulla</Button>
            <Button onClick={handleExportData} disabled={loading} className="btn-primary">
              {loading ? 'Elaborazione...' : 'Conferma esportazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Elimina account e dati</DialogTitle>
            <DialogDescription>Questa azione è irreversibile.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">Attenzione:</p>
              <ul className="mt-2 text-sm text-red-700 space-y-1">
                <li>• Perderai l'accesso ai tuoi preferiti</li>
                <li>• Le partecipazioni in corso saranno annullate</li>
                <li>• Le caparre non saranno rimborsate</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Annulla</Button>
            <Button variant="destructive" onClick={handleDeleteAccount} disabled={loading} className="bg-red-600 hover:bg-red-700">
              {loading ? 'Elaborazione...' : 'Conferma eliminazione'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Dashboard Component
const BuyerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-white">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      {/* Email Verification Banner */}
      <div className="lg:ml-72">
        <EmailVerificationBanner user={user} />
      </div>
      
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-30 glass-header px-6 py-4">
        <div className="flex items-center justify-between">
          <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2 rounded-xl hover:bg-[#F5F5F7] transition-colors">
            <Menu className="h-6 w-6 text-[#1D1D1F]" strokeWidth={1.5} />
          </button>
          <span className="text-lg font-semibold text-[#1D1D1F] tracking-tight">WEQASA</span>
          <div className="w-10" />
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-72 p-6 lg:p-10">
        <Routes>
          <Route index element={<DashboardOverview />} />
          <Route path="profilo" element={<ProfilePage />} />
          <Route path="sicurezza" element={<SecurityPage />} />
          <Route path="privacy" element={<BuyerPrivacyDashboardPage />} />
          <Route path="preferiti" element={<FavoritesPage />} />
          <Route path="visite" element={<VisitsPage />} />
          <Route path="gare" element={<BuyerGarePage />} />
          <Route path="partecipazioni" element={<ParticipationsPage />} />
          <Route path="offerte" element={<BidsHistoryPage />} />
        </Routes>
      </div>
    </div>
  );
};

export default BuyerDashboard;
